import { z } from 'zod';
import { formatDate } from '../../helpers/formatDate';

/**
 * Hotel Metrics Widget Schema
 * Validates data from backend's hotel-metrics-data SSE stream
 *
 * Backend sends metrics for multiple KPIs (occupancy, revenue, ADR, RevPAR, bookings)
 * with STLY comparison and rich metadata for formatting.
 */

/**
 * Column metadata schema
 * Describes a single metric (e.g., occupancy, revenue)
 */
const ColumnMetadataSchema = z.object({
  key: z.string(), // e.g., "occupancy", "revenue"
  displayName: z.string(), // e.g., "Occupancy", "Revenue"
  dataType: z.enum(['percentage', 'currency', 'count']),
  unit: z.string(), // e.g., "%", "$", "bookings"
  category: z.enum(['performance', 'financial']),
  aggregation: z.enum(['avg', 'sum']),
  format: z.object({
    decimals: z.number(),
    prefix: z.string().optional(),
    suffix: z.string().optional(),
  }),
});

export type ColumnMetadata = z.infer<typeof ColumnMetadataSchema>;

/**
 * Time dimension metadata
 */
const TimeDimensionSchema = z.object({
  type: z.literal('date-range'),
  startDate: z.string(), // YYYY-MM-DD
  endDate: z.string(), // YYYY-MM-DD
  dayCount: z.number().min(1).max(90), // Support up to 90 days
  timezone: z.string().optional(),
});

export type TimeDimension = z.infer<typeof TimeDimensionSchema>;

/**
 * Metrics for a single day
 * Dynamic object containing metric_key -> value mappings
 */
const DailyMetricsSchema = z.record(z.number());

export type DailyMetrics = z.infer<typeof DailyMetricsSchema>;

/**
 * Time series data point (one day)
 */
const TimeSeriesPointSchema = z.object({
  date: z.string(), // YYYY-MM-DD
  dayOfWeek: z.string(), // e.g., "Monday", "Tuesday"
  metrics: DailyMetricsSchema,
});

export type TimeSeriesPoint = z.infer<typeof TimeSeriesPointSchema>;

/**
 * Summary metrics (aggregated over the period)
 */
const SummaryMetricsSchema = z.record(z.number());

export type SummaryMetrics = z.infer<typeof SummaryMetricsSchema>;

/**
 * Current period data
 */
const CurrentPeriodSchema = z.object({
  timeSeries: z.array(TimeSeriesPointSchema).min(1).max(90),
  summary: SummaryMetricsSchema,
});

export type CurrentPeriod = z.infer<typeof CurrentPeriodSchema>;

/**
 * STLY period data (optional)
 */
const STLYPeriodSchema = z.object({
  enabled: z.boolean(),
  timeSeries: z.array(TimeSeriesPointSchema).max(90).optional(),
  summary: SummaryMetricsSchema.optional(),
});

export type STLYPeriod = z.infer<typeof STLYPeriodSchema>;

/**
 * Delta values for a single day
 */
const DailyDeltasSchema = z.record(z.number());

export type DailyDeltas = z.infer<typeof DailyDeltasSchema>;

/**
 * Comparison time series point
 */
const ComparisonTimeSeriesPointSchema = z.object({
  date: z.string(),
  deltas: DailyDeltasSchema,
});

export type ComparisonTimeSeriesPoint = z.infer<typeof ComparisonTimeSeriesPointSchema>;

/**
 * Comparison data (current vs STLY)
 */
const ComparisonSchema = z.object({
  timeSeries: z.array(ComparisonTimeSeriesPointSchema).optional(),
  summary: z.record(z.number()),
});

export type Comparison = z.infer<typeof ComparisonSchema>;

/**
 * Display hints from backend
 */
const DisplayHintsSchema = z.object({
  defaultMetric: z.string(),
  suggestedChartType: z.string().optional(),
});

export type DisplayHints = z.infer<typeof DisplayHintsSchema>;

/**
 * STLY completeness metadata
 */
const STLYCompletenessSchema = z.object({
  totalDays: z.number(),
  stlyAvailable: z.number(),
  percentage: z.number(),
});

export type STLYCompleteness = z.infer<typeof STLYCompletenessSchema>;

/**
 * Widget metadata
 */
const MetadataSchema = z.object({
  generatedBy: z.string().optional(),
  requestId: z.string().optional(),
  generatedAt: z.string().optional(),
  currency: z.string().optional(),
  stlyCompleteness: STLYCompletenessSchema.optional(),
});

export type Metadata = z.infer<typeof MetadataSchema>;

/**
 * Main hotel metrics widget schema
 */
export const HotelMetricsWidgetSchema = z.object({
  type: z.literal('hotel-metrics-data'),
  version: z.string(),
  columns: z.array(ColumnMetadataSchema).min(1).max(20),
  timeDimension: TimeDimensionSchema,
  current: CurrentPeriodSchema,
  stly: STLYPeriodSchema,
  comparison: ComparisonSchema.optional(),
  displayHints: DisplayHintsSchema.optional(),
  metadata: MetadataSchema.optional(),
});

export type HotelMetricsWidget = z.infer<typeof HotelMetricsWidgetSchema>;

/**
 * Safe parse function
 */
export function safeParseHotelMetrics(json: unknown) {
  return HotelMetricsWidgetSchema.safeParse(json);
}

/**
 * Helper: Get column metadata by key
 */
export function getColumnMetadata(
  widget: HotelMetricsWidget,
  key: string
): ColumnMetadata | undefined {
  return widget.columns.find((col) => col.key === key);
}

/**
 * Helper: Format metric value using column metadata
 * Follows formatting rules:
 * - Zero decimals unless value is less than 1
 * - No decimals for percentages (unless < 1)
 */
export function formatMetricValue(
  value: number,
  column: ColumnMetadata
): string {
  // Handle invalid numbers
  if (!Number.isFinite(value)) {
    return 'N/A';
  }

  // Apply consistent decimal rules:
  // - Zero decimals unless value is less than 1
  let decimals = column.format.decimals;
  if (Math.abs(value) >= 1) {
    decimals = 0;
  } else if (Math.abs(value) < 1 && Math.abs(value) > 0) {
    // For values < 1, allow 1 decimal max
    decimals = Math.min(1, decimals);
  }

  const formatted = value.toFixed(decimals);
  const prefix = column.format.prefix || '';
  const suffix = column.format.suffix || '';
  return `${prefix}${formatted}${suffix}`;
}

/**
 * Helper: Get short day label from dayOfWeek
 * "Monday" -> "Mon", "Tuesday" -> "Tue", etc.
 */
export function getShortDayLabel(dayOfWeek: string): string {
  return dayOfWeek.substring(0, 3);
}

/**
 * Helper: Format date for display
 * "2025-01-15" -> "1/15/2025" (M/D/YYYY format)
 *
 * Delegates to the shared `formatDate(..., "numeric")` utility (audit UX-05).
 * Preserves this function's original contract of echoing the raw input back
 * when it isn't a valid date (the shared util returns "" for invalid, so we
 * fall back to `dateStr`).
 */
export function formatDateDisplay(dateStr: string): string {
  return formatDate(dateStr, 'numeric') || dateStr;
}
